import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Clock, Server, BookOpenCheck, Loader2 } from "lucide-react";
import { SERVICENOW_CURRICULUM } from "@/data/servicenowCurriculumData";
import api from "@/lib/api";

export default function ServiceNowCurriculumMapPage() {
  const availableCategories = SERVICENOW_CURRICULUM.filter((c) => c.status === "available");
  const libraryCategories = SERVICENOW_CURRICULUM.filter((c) => c.status === "library");
  const totalTopics = SERVICENOW_CURRICULUM.reduce((sum, c) => sum + c.topics.length, 0);
  const coveredTopics = availableCategories.reduce((sum, c) => sum + c.topics.filter((t) => t.day).length, 0);

  const [libraryProgress, setLibraryProgress] = useState(null);
  const [loadingLibrary, setLoadingLibrary] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/servicenow/extended-library");
        const map = {};
        data.categories.forEach((cat) => cat.topics.forEach((t) => { map[t.slug] = t; }));
        setLibraryProgress({ map, completed_count: data.completed_count, total_topics: data.total_topics });
      } catch {
        setLibraryProgress(null);
      } finally {
        setLoadingLibrary(false);
      }
    };
    load();
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="servicenow-curriculum-map-page">
      <Link to="/servicenow" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to ServiceNow Roadmap
      </Link>

      <div className="flex items-center gap-3 mb-2">
        <Server className="w-6 h-6 text-emerald-400" />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">Full ServiceNow Curriculum</span>
      </div>
      <h1 className="font-heading text-3xl font-semibold text-white">ServiceNow Curriculum Map</h1>
      <p className="text-sm text-slate-400 mt-2 max-w-3xl">
        The complete ServiceNow topic taxonomy across all 16 categories. {coveredTopics} of {totalTopics} topics are already live inside
        the 30-Day Challenge (Platform Fundamentals, Developer, ITSM, AI & Automation, Certification Prep) — click any covered topic to jump straight to its Day.
        The remaining 11 categories are a browsable <span className="text-emerald-400 font-semibold">Topic Library</span> — click any topic to read real content and take a quick knowledge check
        {libraryProgress && ` (${libraryProgress.completed_count}/${libraryProgress.total_topics} completed)`}.
      </p>

      <div className="mt-8 space-y-6">
        {availableCategories.map((cat) => (
          <div key={cat.category} className="glass-panel rounded-2xl p-6" data-testid={`curriculum-category-${cat.category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-semibold text-white">{cat.category}</h2>
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 bg-emerald-400/10 px-2.5 py-1 rounded-full">Live in Challenge</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {cat.topics.map((t) => (
                t.day ? (
                  <Link
                    key={t.title} to={`/servicenow/challenge/${t.day}`}
                    data-testid={`curriculum-topic-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="flex items-center gap-2 text-sm px-3 py-2.5 rounded-lg border border-emerald-400/20 bg-emerald-400/[0.03] hover:border-emerald-400/50 hover:bg-emerald-400/[0.07] transition-colors text-slate-200"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                    <span className="flex-1">{t.title}</span>
                    <span className="text-[10px] font-bold text-emerald-400 shrink-0">DAY {t.day}</span>
                  </Link>
                ) : (
                  <div key={t.title} className="flex items-center gap-2 text-sm px-3 py-2.5 rounded-lg border border-white/5 bg-white/[0.01] text-slate-400">
                    <Clock className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                    <span className="flex-1">{t.title}</span>
                  </div>
                )
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 mb-4 flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg font-semibold text-white">Extended Topic Library</h2>
          <p className="text-sm text-slate-400 mt-1">11 additional ServiceNow product lines — click any topic for real content + a quick knowledge check.</p>
        </div>
        {loadingLibrary && <Loader2 className="w-4 h-4 text-emerald-400 animate-spin" />}
      </div>
      <div className="space-y-4" data-testid="curriculum-library-grid">
        {libraryCategories.map((cat) => (
          <div key={cat.category} className="glass-panel rounded-2xl p-5" data-testid={`curriculum-category-${cat.category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-white">{cat.category}</h3>
              <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded-full">Topic Library</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {cat.topics.map((t) => {
                const prog = libraryProgress?.map?.[t.slug];
                return (
                  <Link
                    key={t.slug} to={`/servicenow-library/${t.slug}`}
                    data-testid={`curriculum-library-topic-${t.slug}`}
                    className={`flex items-center gap-2 text-xs px-3 py-2.5 rounded-lg border transition-colors ${prog?.completed ? "border-emerald-400/40 bg-emerald-400/[0.06] text-slate-200" : "border-white/5 bg-white/[0.02] text-slate-300 hover:border-emerald-400/40 hover:bg-emerald-400/[0.04]"}`}
                  >
                    {prog?.completed ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> : <BookOpenCheck className="w-3.5 h-3.5 text-slate-500 shrink-0" />}
                    <span className="flex-1">{t.title}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

